import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Link, useNavigate } from "react-router-dom";
import { 
    Button, 
    TextField, 
    Typography, 
    Checkbox, 
    Grid, 
    Container, 
    Box, 
    Card, 
    CardContent, 
    Paper,
    Avatar,
    alpha,
    Fade,
    Slide
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { CLIENT_ID, LOGIN_HUB_URL } from "../../../config";
import { 
    AdminPanelSettings as AdminIcon,
    Login as LoginIcon,
    Security as SecurityIcon,
    Dashboard as DashboardIcon
} from "@mui/icons-material";
import Cookies from "js-cookie";

// Styled components for better visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.95)',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1.1rem',
    padding: '12px 32px',
    boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 25px rgba(25, 118, 210, 0.4)',
    },
}));

const BackgroundBox = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    },
}));

const FeatureBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: 8,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
}));

const LoginPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Check if user is already logged in
        const adminId = Cookies.get("admin_id");
        if (adminId) {
            navigate("/admin"); // Redirect to dashboard if logged in
        }
    }, [navigate]);

    const handleLoginEmail = async () => {
        setIsLoading(true);
        try {
       // const baseURL = "http://customer-api.secureivs.eu/";
       // const baseURL = "https://customer-api.theloginhub.eu/";
       // const clientId = "VbQu8HRymrbHktZ9HPUH97R5DGoubf";  // dev
       // const clientId = "qMpkpVQPBadSLwmREGRbu2lsoqCzeZ";  // live
        const loginMethod = "email";
        const redirectURI = `${window.location.origin}/verify-admin`;

        const loginURL = `${LOGIN_HUB_URL}?client_id=${CLIENT_ID}&login_method=${loginMethod}&redirect_uri=${encodeURIComponent(
            redirectURI
        )}`;
        window.location.href = loginURL;
        } catch (error) {
            console.error("Login error:", error);
            setIsLoading(false);
        }
    };

  return (
            <BackgroundBox>
                <Fade in={true} timeout={1000}>
                    <Container maxWidth="sm">
                        <Grid container spacing={4} alignItems="center" justifyContent="center">
                            {/* Centered Login Form */}
                            <Grid item xs={12} sm={8} md={6}>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Typography variant="h2" gutterBottom sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
                                        Admin Dashboard
                                    </Typography>
                                    <Typography variant="h6" sx={{ color: 'white', opacity: 0.9, mb: 4 }}>
                                        Manage your coupon campaigns, users, and analytics
                                    </Typography>
                                </Box>
                                <Slide direction="up" in={true} timeout={800}>
                                    <StyledCard>
                                        <CardContent sx={{ p: 4 }}>
                                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                                <Avatar sx={{ 
                                                    width: 80, 
                                                    height: 80, 
                                                    bgcolor: 'primary.main', 
                                                    mx: 'auto', 
                                                    mb: 2,
                                                    boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)'
                                                }}>
                                                    <AdminIcon sx={{ fontSize: 40 }} />
                                                </Avatar>
                                                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    Admin Login
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                                                    Sign in to access your admin dashboard
                                                </Typography>
                                            </Box>

                                            <Box sx={{ textAlign: 'center' }}>
                                                <StyledButton
                                                    variant="contained"
                                                    size="large"
                                                    fullWidth
                                                    onClick={handleLoginEmail}
                                                    disabled={isLoading}
                                                    startIcon={<LoginIcon />}
                                                    sx={{ mb: 3 }}
                                                >
                                                    {isLoading ? 'Redirecting...' : 'Login with Email'}
                                                </StyledButton>

                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                                                    Secure login powered by The Login Hub
                                                </Typography>
                                            </Box>

                                            {/* Additional Info */}
                                            {/* <Box sx={{ mt: 4, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                                    🔒 Your session is protected with enterprise-grade security
                                                </Typography>
                                            </Box> */}
                                        </CardContent>
                                    </StyledCard>
                                </Slide>
                            </Grid>
                        </Grid>
    </Container>
                </Fade>
            </BackgroundBox>
  );
};

export default LoginPage;
